/**
 * Airtable REST API Client
 * 
 * Enhanced REST API client with caching
 * Handles CRUD operations for all Airtable tables
 * Implements rate limit handling and error management
 */

import { ENDPOINTS, AIRTABLE_TABLES, airtableConfig } from './config';
import { 
  getCachedData, 
  setCachedData, 
  invalidateCache, 
  preloadCache 
} from './cache';
import { respectRateLimit } from './rate-limit';
import { 
  isAirtableError,
  AirtableApiError, 
  processApiResponse,
  logAirtableError,
  wrapError,
  getUserFriendlyMessage
} from './errors';
import { 
  withRetry, 
  withRetryWrapper 
} from './retry';

// Types
export interface AirtableRecord {
  id: string;
  fields: Record<string, any>;
  createdTime?: string;
}

export interface AirtableResponse {
  records: AirtableRecord[];
  offset?: string;
}

export interface QueryOptions {
  fields?: string[];
  filterByFormula?: string;
  sort?: Array<{ field: string; direction: 'asc' | 'desc' }>;
  maxRecords?: number;
  view?: string;
  pageSize?: number;
  offset?: string;
  skipCache?: boolean;
  cacheTTL?: number;
}

/**
 * Generate a cache key from table name and options
 */
function generateCacheKey(tableName: string, options: QueryOptions): string {
  return `rest:${tableName}:${JSON.stringify(options)}`;
}

/**
 * Build URL with query parameters
 */
function buildUrl(tableName: string, options: QueryOptions): string {
  const baseId = airtableConfig.baseId;
  const baseUrl = `${ENDPOINTS.REST_API}/${baseId}/${encodeURIComponent(tableName)}`;
  const params = new URLSearchParams();
  
  if (options.fields?.length) {
    options.fields.forEach(field => params.append('fields[]', field));
  }
  
  if (options.filterByFormula) {
    params.append('filterByFormula', options.filterByFormula);
  }
  
  if (options.sort?.length) {
    options.sort.forEach((sortItem, index) => {
      params.append(`sort[${index}][field]`, sortItem.field);
      params.append(`sort[${index}][direction]`, sortItem.direction);
    });
  }
  
  if (options.maxRecords && typeof options.maxRecords.toString === 'function') {
    params.append('maxRecords', options.maxRecords.toString());
  } else if (options.maxRecords) {
    params.append('maxRecords', String(options.maxRecords));
  }
  
  if (options.view) {
    params.append('view', options.view);
  }
  
  if (options.pageSize && typeof options.pageSize.toString === 'function') {
    params.append('pageSize', options.pageSize.toString());
  } else if (options.pageSize) {
    params.append('pageSize', String(options.pageSize));
  }
  
  if (options.offset) {
    params.append('offset', options.offset);
  }
  
  const queryString = params && typeof params.toString === 'function' ? params.toString() : String(params);
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

/**
 * Generate endpoint identifier for retry/fallback
 */
function getEndpointId(tableName: string, operation: string): string {
  return `airtable:${tableName}:${operation}`;
}

/**
 * Basic fetch records without retry or fallback
 */
async function baseFetchRecords(
  tableName: string,
  options: QueryOptions = {}
): Promise<AirtableResponse> {
  await respectRateLimit();
  
  // Check cache if not explicitly skipped
  if (!options.skipCache) {
    const cacheKey = generateCacheKey(tableName, options);
    const cachedData = await getCachedData<AirtableResponse>(cacheKey);
    
    if (cachedData) {
      // Log first record fields if present
      if (cachedData.records && cachedData.records.length > 0) {
        console.warn(`[Airtable][baseFetchRecords][CACHE] First record fields for table '${tableName}':`, Object.keys(cachedData.records[0].fields));
        console.warn(`[Airtable][baseFetchRecords][CACHE] First record values:`, cachedData.records[0].fields);
      }
      return cachedData;
    }
  }
  
  try {
    const url = buildUrl(tableName, options);
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${process.env.NEXT_PUBLIC_AIRTABLE_PAT || process.env.AIRTABLE_PAT}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      cache: 'no-store', // For Next.js, don't cache at the fetch level
    });
    
    const data = await processApiResponse<AirtableResponse>(response, { tableName, options });
    
    // Log first record fields if present
    if (data.records && data.records.length > 0) {
      console.warn(`[Airtable][baseFetchRecords][API] First record fields for table '${tableName}':`, Object.keys(data.records[0].fields));
      console.warn(`[Airtable][baseFetchRecords][API] First record values:`, data.records[0].fields);
    }
    
    // Store in cache if caching is not skipped
    if (!options.skipCache) {
      const cacheKey = generateCacheKey(tableName, options);
      await setCachedData(cacheKey, data);
    }
    
    return data;
  } catch (error) {
    // Convert to proper AirtableError and re-throw
    throw wrapError(error, { operation: 'fetchRecords', tableName, options });
  }
}

/**
 * Basic create record without retry or fallback
 */
async function baseCreateRecord(
  tableName: string,
  fields: Record<string, any>
): Promise<AirtableRecord> {
  await respectRateLimit();
  
  try {
    const url = `${ENDPOINTS.REST_API}/${encodeURIComponent(tableName)}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${process.env.NEXT_PUBLIC_AIRTABLE_PAT || process.env.AIRTABLE_PAT}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ fields }),
    });
    
    const data = await processApiResponse<AirtableRecord>(response, { tableName, fields });
    
    // Invalidate related cache entries
    await invalidateCache(`rest:${tableName}:`);
    
    return data;
  } catch (error) {
    // Convert to proper AirtableError and re-throw
    throw wrapError(error, { operation: 'createRecord', tableName, fields });
  }
}

/**
 * Basic update record without retry or fallback
 */
async function baseUpdateRecord(
  tableName: string,
  recordId: string,
  fields: Record<string, any>
): Promise<AirtableRecord> {
  await respectRateLimit();
  
  try {
    const url = `${ENDPOINTS.REST_API}/${encodeURIComponent(tableName)}/${recordId}`;
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${process.env.NEXT_PUBLIC_AIRTABLE_PAT || process.env.AIRTABLE_PAT}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ fields }),
    });
    
    const data = await processApiResponse<AirtableRecord>(response, { tableName, recordId, fields });
    
    // Invalidate related cache entries
    await invalidateCache(`rest:${tableName}:`);
    
    return data;
  } catch (error) {
    // Convert to proper AirtableError and re-throw
    throw wrapError(error, { operation: 'updateRecord', tableName, recordId, fields });
  }
}

/**
 * Basic delete record without retry or fallback
 */
async function baseDeleteRecord(
  tableName: string,
  recordId: string
): Promise<{ id: string; deleted: boolean }> {
  await respectRateLimit();
  
  try {
    const url = `${ENDPOINTS.REST_API}/${encodeURIComponent(tableName)}/${recordId}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${process.env.NEXT_PUBLIC_AIRTABLE_PAT || process.env.AIRTABLE_PAT}`,
        'Accept': 'application/json'
      },
    });
    
    const data = await processApiResponse<{ id: string; deleted: boolean }>(
      response, 
      { tableName, recordId }
    );
    
    // Invalidate related cache entries
    await invalidateCache(`rest:${tableName}:`);
    
    return data;
  } catch (error) {
    // Convert to proper AirtableError and re-throw
    throw wrapError(error, { operation: 'deleteRecord', tableName, recordId });
  }
}

// ------------------- Enhanced API Functions (with retry & fallback) -------------------

/**
 * Fetch records with retry and fallback capabilities
 */
export async function fetchRecords(
  tableName: string,
  options: QueryOptions = {}
): Promise<AirtableResponse> {
  // Log fetch call
  console.warn(`[Airtable][fetchRecords] Fetching records for table '${tableName}' with options:`, options);
  const cacheKey = generateCacheKey(tableName, options);
  const endpointId = getEndpointId(tableName, 'fetchRecords');
  // Normal flow with retry
  try {
    const result = await withRetry(
      () => baseFetchRecords(tableName, options),
      endpointId
    );
    let records: any[] | undefined = undefined;
    if (typeof result === 'object' && result !== null) {
      if ('data' in result && result.data && typeof result.data === 'object' && result.data !== null && 'records' in result.data) {
        records = (result.data as { records?: any[] }).records as any[] | undefined;
      } else if ('records' in result) {
        records = (result as { records?: any[] }).records as any[] | undefined;
      }
    }
    if (Array.isArray(records) && records.length > 0) {
      console.warn(`[Airtable][fetchRecords] First record fields for table '${tableName}':`, Object.keys(records[0].fields));
      console.warn(`[Airtable][fetchRecords] First record values:`, records[0].fields);
    }
    if (
      typeof result === 'object' &&
      result !== null
    ) {
      if (
        'data' in result &&
        typeof (result as any).data === 'object' &&
        (result as any).data !== null &&
        Array.isArray(((result as any).data).records)
      ) {
        return (result as any).data as AirtableResponse;
      }
    }
    throw new Error('No valid AirtableResponse found in fetchRecords');
  } catch (error) {
    // If retry failed, just log and rethrow
    logAirtableError(error as Error, { tableName, options });
    throw new Error(getUserFriendlyMessage(error));
  }
}

/**
 * Create record with retry (no fallback - non-idempotent operation)
 */
export async function createRecord(
  tableName: string,
  fields: Record<string, any>
): Promise<AirtableRecord> {
  const endpointId = getEndpointId(tableName, 'createRecord');
  
  try {
    return await withRetry(
      () => baseCreateRecord(tableName, fields),
      endpointId
    );
  } catch (error) {
    // For write operations, just log and rethrow with user-friendly message
    const wrappedError = wrapError(error, { 
      tableName, 
      fields,
      userMessage: getUserFriendlyMessage(error)
    });
    
    logAirtableError(wrappedError);
    throw wrappedError;
  }
}

/**
 * Update record with retry (no fallback - non-idempotent operation)
 */
export async function updateRecord(
  tableName: string,
  recordId: string,
  fields: Record<string, any>
): Promise<AirtableRecord> {
  const endpointId = getEndpointId(tableName, 'updateRecord');
  
  try {
    return await withRetry(
      () => baseUpdateRecord(tableName, recordId, fields),
      endpointId
    );
  } catch (error) {
    // For write operations, just log and rethrow with user-friendly message
    const wrappedError = wrapError(error, { 
      tableName,
      recordId, 
      fields,
      userMessage: getUserFriendlyMessage(error)
    });
    
    logAirtableError(wrappedError);
    throw wrappedError;
  }
}

/**
 * Delete record with retry (no fallback - non-idempotent operation)
 */
export async function deleteRecord(
  tableName: string,
  recordId: string
): Promise<{ id: string; deleted: boolean }> {
  const endpointId = getEndpointId(tableName, 'deleteRecord');
  
  try {
    return await withRetry(
      () => baseDeleteRecord(tableName, recordId),
      endpointId
    );
  } catch (error) {
    // For write operations, just log and rethrow with user-friendly message
    const wrappedError = wrapError(error, { 
      tableName,
      recordId,
      userMessage: getUserFriendlyMessage(error)
    });
    
    logAirtableError(wrappedError);
    throw wrappedError;
  }
}

/**
 * Preload frequently accessed tables into the cache
 * This improves performance for common data that is used throughout the application
 */
export async function preloadTables(): Promise<{ success: boolean; loaded: number }> {
  console.log('Preloading Airtable tables into cache...');
  // Using the preloadCache function from the cache module
  // It will use the preloadTables config setting to determine which tables to load
  return preloadCache(async (logicalKey: string) => {
    const tableId = AIRTABLE_TABLES[logicalKey]?.id;
    if (!tableId) {
      console.warn(`[Preload] No table ID found for logical key: ${logicalKey}`);
      return;
    }
    console.log(`Preloading table: ${logicalKey} (ID: ${tableId})`);
    try {
      const { dynamicDeserialize } = await import('./transform');
      const result = await fetchRecords(tableId, {
        maxRecords: 100,
        skipCache: true
      });
      console.log(`[Preload] Transforming ${result.records.length} records from ${logicalKey}`);
      const transformedRecords = await Promise.all(
        result.records.map(rec => dynamicDeserialize(tableId, rec))
      );
      console.log(`[Preload] Transformed records for ${logicalKey}. Sample:`, transformedRecords[0]);
      const transformedResult = {
        ...result,
        records: transformedRecords
      };
      console.log(`Preloaded ${transformedResult.records.length} records from ${logicalKey}`);
      return transformedResult;
    } catch (error) {
      console.error(`[Preload] Error transforming records for ${logicalKey}:`, error);
      throw error;
    }
  });
}

// NOTE: Preloading tables must only be triggered from server-only code (e.g., API routes, server init scripts).
// Do NOT run preloadTables at the top level, or it will execute in the browser bundle and cause errors. 
